import { Link, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AuthCard } from './AuthCard';
import { auth } from '../../api/endpoints';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Skeleton } from '../../components/Skeleton';
import { useT } from '../../i18n/provider';
import { REGISTRATION_OPEN } from '../../lib/registration';

/**
 * The screen a verification link lands on. It verifies immediately rather than
 * showing a button: the user already clicked one, in their email.
 */
export function VerifyEmailScreen() {
  const t = useT();
  const search = useSearch({ strict: false }) as { token?: string };
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [error, setError] = useState<unknown>(null);

  /**
   * A verification token is single-use, so this request must happen exactly
   * once per token — and an effect is not a once-per-mount guarantee. React's
   * StrictMode runs it twice in development, which spent the token on the first
   * call and showed the user the failure from the second; the same shape breaks
   * for real on any remount. The ref records which token has already been
   * attempted rather than merely "have we run", so a genuinely different link
   * in the same session is still tried.
   */
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (attempted.current === (search.token ?? null)) {
      return;
    }
    attempted.current = search.token ?? null;

    if (!search.token) {
      setState('failed');
      setError(new Error(t('auth.verifyEmail.missingToken')));

      return;
    }

    auth
      .verifyEmail(search.token)
      .then(() => setState('done'))
      .catch((caught: unknown) => {
        setError(caught);
        setState('failed');
      });
  }, [search.token, t]);

  if (state === 'working') {
    return (
      <AuthCard title={t('auth.verifyEmail.workingTitle')}>
        <Skeleton className="h-10 w-full" />
      </AuthCard>
    );
  }

  if (state === 'failed') {
    return (
      <AuthCard title={t('auth.verifyEmail.failedTitle')} subtitle={t('auth.verifyEmail.failedSubtitle')}>
        {error ? <ErrorBanner error={error} /> : null}
        {REGISTRATION_OPEN ? (
          <>
            <p className="text-sm text-ink-soft">{t('auth.verifyEmail.failedOpenHint')}</p>
            <Link to="/register" className="text-sm font-semibold text-primary">
              {t('auth.verifyEmail.requestNewLink')}
            </Link>
          </>
        ) : (
          // Sin alta pública, reenviar el enlace no es algo que el visitante
          // pueda provocar: la cuenta la creó el operador y es quien puede
          // volver a emitirlo.
          <p className="text-sm text-ink-soft">{t('auth.verifyEmail.failedClosedHint')}</p>
        )}
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.verifyEmail.doneTitle')} subtitle={t('auth.verifyEmail.doneSubtitle')}>
      <Link to="/login" className="text-sm font-semibold text-primary">
        {t('auth.signIn')}
      </Link>
    </AuthCard>
  );
}
